import {
  rgb,
  PDFDocument,
  PDFFont,
  PDFPage,
  pushGraphicsState,
  moveTo,
  lineTo,
  closePath,
  setFillingColor,
  fill,
  popGraphicsState,
  PDFImage,
} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { addPontis, capitalizeFirstLetter } from '../../../store/utils';
import { CSSProperties } from 'react';
import QRCode from 'qrcode-generator';
import JsBarcode from 'jsbarcode';
import { rotatePoint } from '../../../store/form/utils';
import {
  splitAndIdentifyPow,
  splitByIndices,
} from '../../../store/banner/utils';

type HorizontalMarginType = 'left' | 'right' | 'center';
type VerticalMarginType = 'top' | 'bottom' | 'center';
export type MarginType = [HorizontalMarginType, VerticalMarginType];
export type Font = {
  fontFamily: string;
  fontWeight: string;
  buffer: ArrayBuffer;
};
interface NodeProps {
  price?: number;
  text?: string;
  code?: string;
  top?: number;
  left?: number;
  right?: number;
  width?: number;
  size?: number;
  height?: number;
  margin?: MarginType;
  textAlign?: CSSProperties['textAlign'];
  alignItems?: CSSProperties['alignItems'];
  nowrap?: boolean;
  color?: [number, number, number];
  variant?: 'primary' | 'secondary' | 'primaryOld' | 'secondaryOld';
  background?: [number, number, number, number];
  rotate?: number;
  fontFamily: string;
  fontWeight: string;
  params?: Array<string | number>;
  backgroundImage?: string;
  borderRadius?: CSSProperties['borderRadius'];
}

export default class PdfBanner {
  pdfDoc?: PDFDocument;
  page?: PDFPage;
  fonts?: {
    [key: string]: {
      [key: string]: PDFFont;
    };
  };
  images: any = {
    xOldPrice: null,
  };
  environment: 'production' | 'development' = 'production';
  minFontSize = 1;
  maxFontSize = 1000;

  public async qrcodeNode({
    text = 'qrcode',
    left,
    top = 0,
    size = 0,
    right,
  }: NodeProps) {
    if (!size) return;
    const { width: pageWidth, height: pageHeight } = this.page!.getSize();
    const qrcode = QRCode(0, 'H');
    qrcode.addData(text, 'Byte');
    qrcode.make();
    var canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      const margin = size * 0.075;
      const moduleCount = qrcode.getModuleCount();
      const cellSize = 300 / moduleCount;
      const borderRadius = cellSize;
      const x = left ? left : right ? pageWidth - right - size : 0;
      const y = pageHeight - size - top;
      qrcode.renderTo2dContext(ctx, cellSize);
      if (this.isDev()) {
        this.page!.drawRectangle({
          x: x - margin,
          y: y - margin,
          width: size,
          height: size,
          borderWidth: 0,
          color: rgb(0.75, 0.2, 0.2),
          opacity: 0.5,
          borderOpacity: 0.75,
        });
      }
      this.drawRoundedRectangle(
        y - margin,
        x - margin,
        size,
        size,
        borderRadius,
        rgb(1, 1, 1),
      );
      const pngImage = await this.pdfDoc!.embedPng(canvas.toDataURL());
      this.page!.drawImage(pngImage, {
        x,
        y,
        width: size - 2 * margin,
        height: size - 2 * margin,
      });
    }
  }

  private async barcodeNodePrimary({
    text = 'barcode',
    code = 'code',
    top = 0,
    left,
    width = 0,
    height = 0,
    right,
    fontFamily = 'Primary',
    fontWeight = 'Bold',
  }: NodeProps) {
    if (!(width && height)) return;
    const { width: pageWidth, height: pageHeight } = this.page!.getSize();
    const margin = height * 0.15;
    const heightFactor = 0.5;
    const borderRadius = margin / 2;
    var canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    if (text)
      JsBarcode(canvas, text, {
        displayValue: false,
        margin: 0,
        width: 1,
      });
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      const offsetX = left ? left : right ? pageWidth - right - width : 0;
      const offsetY = pageHeight - top - height;
      if (this.isDev()) {
        this.page!.drawRectangle({
          x: offsetX,
          y: offsetY,
          width: width,
          height: height,
          borderWidth: 0,
          color: rgb(0.75, 0.2, 0.2),
          opacity: 0.5,
          borderOpacity: 0.75,
        });
      }

      this.drawRoundedRectangle(
        offsetY,
        offsetX,
        width,
        height,
        borderRadius,
        rgb(1, 1, 1),
      );
      const maxLineHeight = ((1 - heightFactor) / 2) * height;

      const pngImage = await this.pdfDoc!.embedPng(canvas.toDataURL());
      this.page!.drawImage(pngImage, {
        x: offsetX + margin,
        y: offsetY + maxLineHeight,
        width: width - 2 * margin,
        height: height * heightFactor,
      });

      const lineHeightMultiplier = 1 / 1.4;
      for (
        let currentFontSize = this.minFontSize;
        currentFontSize <= this.maxFontSize;
        currentFontSize++
      ) {
        const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(text, currentFontSize);
        const lineHeight = currentFontSize * lineHeightMultiplier;

        if (lineWidth > width || lineHeight > maxLineHeight * 0.7) {
          const fontSize = currentFontSize - 1;
          const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ].widthOfTextAtSize(text, fontSize);
          const lineWidthCode = this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ].widthOfTextAtSize(code, fontSize);
          const lineHeight = fontSize * lineHeightMultiplier;
          if (this.isDev()) {
            this.page!.drawRectangle({
              x: offsetX + (width - lineWidthCode) / 2,
              y: offsetY + height - maxLineHeight,
              width: lineWidthCode,
              height: maxLineHeight,
              borderWidth: 0,
              color: rgb(0.75, 0.2, 0.2),
              opacity: 0.5,
              borderOpacity: 0.75,
            });
            this.page!.drawRectangle({
              x: offsetX + (width - lineWidthCode) / 2,
              y:
                offsetY +
                height -
                maxLineHeight +
                (maxLineHeight - lineHeight) / 2,
              width: lineWidthCode,
              height: lineHeight,
              borderWidth: 0,
              color: rgb(1, 1, 1),
              opacity: 0.5,
              borderOpacity: 0.75,
            });

            this.page!.drawRectangle({
              x: offsetX + (width - lineWidth) / 2,
              y: offsetY + maxLineHeight - maxLineHeight,
              width: lineWidth,
              height: maxLineHeight,
              borderWidth: 0,
              color: rgb(0.75, 0.2, 0.2),
              opacity: 0.5,
              borderOpacity: 0.75,
            });
            this.page!.drawRectangle({
              x: offsetX + (width - lineWidth) / 2,
              y:
                offsetY +
                maxLineHeight -
                maxLineHeight +
                (maxLineHeight - lineHeight) / 2,
              width: lineWidth,
              height: lineHeight,
              borderWidth: 0,
              color: rgb(1, 1, 1),
              opacity: 0.5,
              borderOpacity: 0.75,
            });
          }
          this.page!.drawText(code, {
            x: offsetX + (width - lineWidthCode) / 2,
            y:
              offsetY +
              height -
              maxLineHeight +
              (maxLineHeight - lineHeight) / 2,
            size: fontSize,
            font: this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ],
          });
          this.page!.drawText(text, {
            x: offsetX + (width - lineWidth) / 2,
            y:
              offsetY +
              maxLineHeight -
              maxLineHeight +
              (maxLineHeight - lineHeight) / 2,
            size: fontSize,
            font: this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ],
          });
          break;
        }
      }
    }
  }

  private async barcodeNodeSecondary({
    text = 'barcode',
    code = 'code',
    top = 0,
    left,
    width = 0,
    height = 0,
    right,
    fontFamily = 'Primary',
    fontWeight = 'Bold',
    color: [r, g, b] = [255, 255, 255],
  }: NodeProps) {
    if (!(width && height)) return;
    const color = rgb(r / 255, g / 255, b / 255);

    const { width: pageWidth, height: pageHeight } = this.page!.getSize();
    const margin = height * 0.15;
    const heightFactor = 0.5;
    const borderRadius = margin / 4;
    var canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    if (text)
      JsBarcode(canvas, text, {
        displayValue: false,
        margin: 0,
        width: 1,
      });
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      const offsetX = left ? left : right ? pageWidth - right - width : 0;
      const offsetY = pageHeight - top - height;
      if (this.isDev()) {
        this.page!.drawRectangle({
          x: offsetX,
          y: offsetY,
          width: width,
          height: height,
          borderWidth: 0,
          color: rgb(0.75, 0.2, 0.2),
          opacity: 0.5,
          borderOpacity: 0.75,
        });
      }

      const maxLineHeight = ((1 - heightFactor) / 2) * height;
      const marginFactor = 0.4;
      this.drawRoundedRectangle(
        offsetY + maxLineHeight - marginFactor * margin,
        offsetX + (1 - marginFactor) * margin,
        width - 2 * (1 - marginFactor) * margin,
        height * heightFactor + 2 * marginFactor * margin,
        borderRadius,
        rgb(1, 1, 1),
      );

      const pngImage = await this.pdfDoc!.embedPng(canvas.toDataURL());
      this.page!.drawImage(pngImage, {
        x: offsetX + margin,
        y: offsetY + maxLineHeight,
        width: width - 2 * margin,
        height: height * heightFactor,
      });

      const lineHeightMultiplier = 1 / 1.4;
      for (
        let currentFontSize = this.minFontSize;
        currentFontSize <= this.maxFontSize;
        currentFontSize++
      ) {
        const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(text, currentFontSize);
        const lineHeight = currentFontSize * lineHeightMultiplier;

        if (lineWidth > width || lineHeight > maxLineHeight * 0.7) {
          const fontSize = currentFontSize - 1;
          const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ].widthOfTextAtSize(text, fontSize);
          const lineWidthCode = this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ].widthOfTextAtSize(code, fontSize);
          const lineHeight = fontSize * lineHeightMultiplier;
          if (this.isDev()) {
            this.page!.drawRectangle({
              x: offsetX + (width - lineWidthCode) / 2,
              y: offsetY + height - maxLineHeight,
              width: lineWidthCode,
              height: maxLineHeight,
              borderWidth: 0,
              color: rgb(0.75, 0.2, 0.2),
              opacity: 0.5,
              borderOpacity: 0.75,
            });
            this.page!.drawRectangle({
              x: offsetX + (width - lineWidthCode) / 2,
              y: offsetY + height - lineHeight,
              width: lineWidthCode,
              height: lineHeight,
              borderWidth: 0,
              color: rgb(1, 1, 1),
              opacity: 0.5,
              borderOpacity: 0.75,
            });

            this.page!.drawRectangle({
              x: offsetX + (width - lineWidth) / 2,
              y: offsetY + maxLineHeight - maxLineHeight,
              width: lineWidth,
              height: maxLineHeight,
              borderWidth: 0,
              color: rgb(0.75, 0.2, 0.2),
              opacity: 0.5,
              borderOpacity: 0.75,
            });
            this.page!.drawRectangle({
              x: offsetX + (width - lineWidth) / 2,
              y: offsetY,

              width: lineWidth,
              height: lineHeight,
              borderWidth: 0,
              color: rgb(1, 1, 1),
              opacity: 0.5,
              borderOpacity: 0.75,
            });
          }
          this.page!.drawText(code, {
            x: offsetX + (width - lineWidthCode) / 2,
            y: offsetY + height - maxLineHeight + (maxLineHeight - lineHeight),
            size: fontSize,
            font: this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ],
            color,
          });
          this.page!.drawText(text, {
            x: offsetX + (width - lineWidth) / 2,
            y: offsetY + maxLineHeight - maxLineHeight,
            size: fontSize,
            font: this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ],
            color,
          });
          break;
        }
      }
    }
  }
  public async barcodeNode(props) {
    switch (props.variant ?? 'primary') {
      case 'primary':
        return this.barcodeNodePrimary(props);
      case 'secondary':
        return this.barcodeNodeSecondary(props);
      default:
        return this.barcodeNodePrimary(props);
    }
  }
  public async priceNodePrimary({
    price = 0,
    top = 0,
    left,
    width,
    height,
    right,
    margin = ['center', 'center'],
    color: [r, g, b] = [0, 0, 0],
    variant = 'primary',
    fontFamily = 'Primary',
    fontWeight = 'Bold',
    params,
  }: NodeProps) {
    if (!(width && height)) return;
    const isOld = ['primaryOld'].includes(variant);
    const real = addPontis(Math.floor(price / 100).toString());
    const cent = (price % 100).toString().padStart(2, '0');
    const color = rgb(r / 255, g / 255, b / 255);

    const lineHeightMultiplier = 1 / 0.89;
    const lineFactor = 1 / 1.4;

    for (
      let currentFontSize = this.minFontSize;
      currentFontSize <= this.maxFontSize;
      currentFontSize++
    ) {
      const realWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
        capitalizeFirstLetter(fontWeight)
      ].widthOfTextAtSize(`${real}`, currentFontSize);
      const centWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
        capitalizeFirstLetter(fontWeight)
      ].widthOfTextAtSize(`,${cent}`, currentFontSize * (isOld ? 1 : 0.6));

      const lineHeight = currentFontSize * lineHeightMultiplier;
      const lineWidth = realWidth + centWidth;

      if (lineWidth > width || lineHeight > height) {
        const fontSize = currentFontSize - 1;
        const realWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(`${real}`, fontSize);
        const centWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(`,${cent}`, fontSize * (isOld ? 1 : 0.6));

        const lineWidth = realWidth + centWidth;
        const lineHeight = currentFontSize * lineHeightMultiplier;
        const { width: pageWidth, height: pageHeight } = this.page!.getSize();
        const differenceWidth = width - (realWidth + centWidth);
        const differenceHeight = height - lineHeight;

        const offsetX =
          margin[0] === 'center'
            ? differenceWidth / 2
            : margin[0] === 'right'
              ? differenceWidth
              : 0;

        const offsetY =
          margin[1] === 'center'
            ? differenceHeight / 2
            : margin[1] === 'bottom'
              ? differenceHeight
              : 0;

        const offsetLeft = left ? left : right ? pageWidth - right - width : 0;

        const spaceYFactor =
          ((lineHeightMultiplier - lineFactor) * fontSize) / 2;

        if (this.isDev()) {
          this.page!.drawRectangle({
            x: offsetLeft,
            y: pageHeight - height - top,
            width: width,
            height: height,
            borderWidth: 0,
            color: rgb(0.75, 0.2, 0.2),
            opacity: 0.5,
            borderOpacity: 0.75,
          });

          this.page!.drawRectangle({
            x: offsetLeft + offsetX,
            y: pageHeight - top - lineHeight - offsetY,
            width: lineWidth,
            height: lineHeight,
            borderWidth: 0,
            color: rgb(1, 1, 1),
            opacity: 0.5,
            borderOpacity: 0.75,
          });
        }

        this.page!.drawText(`${real}`, {
          x: offsetLeft + offsetX,
          y: pageHeight - top - lineHeight - offsetY + spaceYFactor,
          size: fontSize,
          color,
          font: this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ],
        });

        this.page!.drawText(`,${cent}`, {
          x: offsetLeft + offsetX + realWidth,
          y:
            pageHeight -
            lineHeight -
            top -
            offsetY +
            spaceYFactor +
            (isOld ? 0 : 1) * (lineHeight - fontSize * lineFactor) * 0.6,
          size: fontSize * (isOld ? 1 : 0.6),
          color,
          font: this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ],
        });
        const unit = params?.[0];
        const unitFontWeight = params?.[1];
        if (unit) {
          const paramFontSize = fontSize * 0.6 * 0.22;
          const cFontFamily = capitalizeFirstLetter(fontFamily);
          const cUnitFontWeight = capitalizeFirstLetter(unitFontWeight);
          let unitFont = this.fonts![cFontFamily][cUnitFontWeight];
          if (!unitFont) {
            const fontBuffer = await fetch(
              `/${cFontFamily}-${cUnitFontWeight}.ttf`,
            ).then(async (res) => await res.arrayBuffer());
            unitFont = await this.pdfDoc!.embedFont(fontBuffer);
            this.fonts![cFontFamily][cUnitFontWeight] = unitFont;
          }

          const paramWidth = unitFont.widthOfTextAtSize(
            `${unit}`,
            paramFontSize,
          );

          const paramMargin = 0.2;
          this.page!.drawText(`${unit}`, {
            x:
              offsetLeft +
              offsetX +
              realWidth +
              paramMargin * centWidth +
              ((1 - paramMargin) * centWidth - paramWidth) / 2,
            y:
              pageHeight -
              lineHeight -
              top -
              offsetY +
              spaceYFactor +
              0.22 * (lineHeight - fontSize * lineFactor),
            size: paramFontSize,
            color,
            font: unitFont,
          });
        }
        if (isOld) {
          const angle = Math.atan((lineHeight - spaceYFactor) / lineWidth);

          this.drawInclinedRectangle(
            offsetLeft + offsetX,
            pageHeight - lineHeight - top - offsetY,
            lineWidth / Math.cos(angle),
            2,
            angle,
            rgb(0, 0, 0),
          );
        }
        break;
      }
    }
  }

  async priceNodeSecondary({
    price = 0,
    top = 0,
    left,
    width,
    height,
    right,
    margin = ['center', 'center'],
    color: [r, g, b] = [0, 0, 0],
    variant = 'secondary',
    fontFamily = 'Secondary',
    fontWeight = 'Bold',
    params,
  }: NodeProps) {
    if (!(width && height)) return;
    const isOld = ['secondaryOld'].includes(variant);

    const real = addPontis(Math.floor(price / 100).toString());
    const cent = (price % 100).toString().padStart(2, '0');
    const color = rgb(r / 255, g / 255, b / 255);

    const lineHeightMultiplier = 1 / 0.89;
    const lineFactor = 1 / 1.4;

    for (
      let currentFontSize = this.minFontSize;
      currentFontSize <= this.maxFontSize;
      currentFontSize++
    ) {
      const realWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
        capitalizeFirstLetter(fontWeight)
      ].widthOfTextAtSize(`${real}`, currentFontSize);
      const centWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
        capitalizeFirstLetter(fontWeight)
      ].widthOfTextAtSize(`,${cent}`, currentFontSize * 0.65);

      const lineHeight = currentFontSize * lineHeightMultiplier;
      const lineWidth = realWidth + centWidth;

      if (lineWidth > width || lineHeight > height) {
        const fontSize = currentFontSize - 1;
        const realWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(`${real}`, fontSize);
        const centWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(`${cent}`, fontSize * 0.65);

        const lineWidth = realWidth + centWidth;
        const lineHeight = currentFontSize * lineHeightMultiplier;
        const { width: pageWidth, height: pageHeight } = this.page!.getSize();
        const differenceWidth = width - (realWidth + centWidth);
        const differenceHeight = height - lineHeight;

        const offsetX =
          margin[0] === 'center'
            ? differenceWidth / 2
            : margin[0] === 'right'
              ? differenceWidth
              : 0;

        const offsetY =
          margin[1] === 'center'
            ? differenceHeight / 2
            : margin[1] === 'bottom'
              ? differenceHeight
              : 0;

        const offsetLeft = left ? left : right ? pageWidth - right - width : 0;

        const spaceYFactor =
          ((lineHeightMultiplier - lineFactor) * fontSize) / 1.5;

        if (this.isDev()) {
          this.page!.drawRectangle({
            x: offsetLeft,
            y: pageHeight - height - top,
            width: width,
            height: height,
            borderWidth: 0,
            color: rgb(0.75, 0.2, 0.2),
            opacity: 0.5,
            borderOpacity: 0.75,
          });

          this.page!.drawRectangle({
            x: offsetLeft + offsetX,
            y: pageHeight - top - lineHeight - offsetY,
            width: lineWidth,
            height: lineHeight,
            borderWidth: 0,
            color: rgb(1, 1, 1),
            opacity: 0.5,
            borderOpacity: 0.75,
          });
        }

        this.page!.drawText(`${real}`, {
          x: offsetLeft + offsetX,
          y: pageHeight - top - lineHeight - offsetY + spaceYFactor,
          size: fontSize,
          color,
          font: this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ],
        });

        this.page!.drawText(`${cent}`, {
          x: offsetLeft + offsetX + realWidth,
          y:
            pageHeight -
            lineHeight -
            top -
            offsetY +
            spaceYFactor +
            0.55 * (lineHeight - fontSize * lineFactor),
          size: fontSize * 0.65,
          color,
          font: this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ],
        });
        this.page!.drawText(',', {
          x: offsetLeft + offsetX + realWidth + 0.03 * centWidth,
          y:
            pageHeight -
            lineHeight -
            top -
            offsetY +
            spaceYFactor +
            0.2 * (lineHeight - fontSize * lineFactor),
          size: fontSize * 0.65,
          color,
          font: this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ],
        });

        if (params) {
          const paramFontSize = (fontSize * 0.65) / 6;

          const paramMargin = 0.2;
          if (['M2'].includes(`${params[0]}`)) {
            const paramWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ].widthOfTextAtSize(
              `${params[0][0].toLocaleLowerCase()}`,
              paramFontSize,
            );
            this.page!.drawText(`${params[0][0].toLocaleLowerCase()}`, {
              x:
                offsetLeft +
                offsetX +
                realWidth +
                paramMargin * centWidth +
                ((1 - paramMargin) * centWidth - paramWidth) / 2,
              y:
                pageHeight -
                lineHeight -
                top -
                offsetY +
                spaceYFactor +
                0.15 * (lineHeight - fontSize * lineFactor),
              size: paramFontSize,
              color,
              font: this.fonts![capitalizeFirstLetter(fontFamily)][
                capitalizeFirstLetter(fontWeight)
              ],
            });
            this.page!.drawText(`${params[0][1]}`, {
              x:
                offsetLeft +
                offsetX +
                realWidth +
                paramMargin * centWidth +
                ((1 - paramMargin) * centWidth + paramWidth) / 2,
              y:
                pageHeight -
                lineHeight -
                top -
                offsetY +
                spaceYFactor +
                0.25 * (lineHeight - fontSize * lineFactor),
              size: paramFontSize * 0.7,
              color,
              font: this.fonts![capitalizeFirstLetter(fontFamily)][
                capitalizeFirstLetter(fontWeight)
              ],
            });
          } else {
            const paramWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ].widthOfTextAtSize(`${params[0]}`, paramFontSize);
            this.page!.drawText(`${params[0]}`, {
              x:
                offsetLeft +
                offsetX +
                realWidth +
                paramMargin * centWidth +
                ((1 - paramMargin) * centWidth - paramWidth) / 2,
              y:
                pageHeight -
                lineHeight -
                top -
                offsetY +
                spaceYFactor +
                0.15 * (lineHeight - fontSize * lineFactor),
              size: paramFontSize,
              color,
              font: this.fonts![capitalizeFirstLetter(fontFamily)][
                capitalizeFirstLetter(fontWeight)
              ],
            });
          }
        }
        if (isOld) {
          await this.page!.drawImage(this.images.xOldPrice, {
            x: offsetLeft + offsetX - 0.3 * lineHeight,
            y: pageHeight - top - lineHeight - offsetY,
            width: lineWidth + 0.6 * lineHeight,
            height: lineHeight,
          });
        }
        break;
      }
    }
  }

  public async priceNode(props: NodeProps) {
    switch (props.variant) {
      case 'primary':
        await this.priceNodePrimary(props);
        break;
      case 'primaryOld':
        await this.priceNodePrimary(props);
        break;
      case 'secondary':
        await this.priceNodeSecondary(props);
        break;
      case 'secondaryOld':
        await this.priceNodeSecondary(props);
        break;
      default:
        await this.priceNodePrimary(props);
        break;
    }
  }

  public textNode({
    text: inText = '',
    top = 0,
    left,
    right,
    width,
    height,
    nowrap = false,
    textAlign = 'center',
    alignItems = 'start',
    color: [r, g, b] = [0, 0, 0],
    background: [br, bg, bb, ba] = [0, 0, 0, 0],
    rotate = 0,
    fontFamily = 'Primary',
    fontWeight = 'Bold',
  }: NodeProps) {
    if (!(width && height)) return;
    const { splitText, pow } = splitAndIdentifyPow(`${inText}`);

    const text = splitText.reduce((acc, acv) => `${acc} ${acv}`, '').trim();
    const color = rgb(r / 255, g / 255, b / 255);
    const { width: pageWidth, height: pageHeight } = this.page!.getSize();
    const words = nowrap ? [text] : text.split(' ');
    const lineHeightMultiplier = 1 / 1.045;
    const lineFactor = 1 / 1.4;
    for (
      let currentFontSize = this.minFontSize;
      currentFontSize <= this.maxFontSize;
      currentFontSize++
    ) {
      const lineHeight =
        this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].heightAtSize(currentFontSize) * lineHeightMultiplier;

      let line = '';
      let result: string[] = [];
      for (let n = 0; n < words.length; n++) {
        const testLine = line + (n > 0 ? ' ' : '') + words[n];
        const testWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(testLine, currentFontSize);
        if (testWidth > width) {
          result.push(line);
          line = words[n];
        } else {
          line = testLine;
        }
      }

      result.push(line);
      result = result.filter((line) => line);
      const textHeight = result.length * lineHeight;
      let maxTextLineWidth = 0;

      result.forEach((line) => {
        const testWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(line, currentFontSize);
        if (testWidth > maxTextLineWidth) maxTextLineWidth = testWidth;
      });

      if (textHeight > height || maxTextLineWidth > width) {
        const fontSize = currentFontSize - 1;
        const lineHeight =
          this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ].heightAtSize(fontSize) * lineHeightMultiplier;
        line = '';
        result = [];
        let pows: [number, number][][] = [[]];
        for (let n = 0; n < words.length; n++) {
          const testLine = line + (n > 0 ? ' ' : '') + words[n];
          const testWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ].widthOfTextAtSize(testLine, fontSize);
          if (testWidth > width) {
            result.push(line);
            pows.push([]);
            line = words[n];
            if (pow[n]) pows[pows.length - 1].push([1, words[n].length + 1]);
          } else {
            if (pow[n])
              pows[pows.length - 1].push([
                line.length + 1,
                line.length + words[n].length + 1,
              ]);
            line = testLine;
          }
        }
        result.push(line);
        result = result.filter((line) => line);

        const spaceYFactor = (lineHeightMultiplier - lineFactor) * fontSize;

        if (ba) {
          this.page!.drawRectangle({
            x: left ? left : right ? pageWidth - right - width : 0,
            y: pageHeight - height - top,
            width: width,
            height: height,
            borderWidth: 0,
            color: rgb(br / 255, bg / 255, bb / 255),
            opacity: ba,
            borderOpacity: ba,
          });
        }
        if (this.isDev()) {
          this.page!.drawRectangle({
            x: left ? left : right ? pageWidth - right - width : 0,
            y: pageHeight - height - top,
            width: width,
            height: height,
            borderWidth: 0,
            color: rgb(0.75, 0.2, 0.2),
            opacity: 0.5,
            borderOpacity: 0.75,
          });
        }
        const offsetY =
          alignItems === 'center'
            ? (height - lineHeight * result.length) / 2
            : 0;

        result.forEach((line, index) => {
          if (this.isDev()) {
            const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ].widthOfTextAtSize(line, fontSize);
            const differenceWidth = width - lineWidth;
            const offsetX =
              textAlign === 'center'
                ? differenceWidth / 2
                : ['right', 'end'].includes(textAlign)
                  ? differenceWidth
                  : 0;
            this.page!.drawRectangle({
              x:
                (left ? left : right ? pageWidth - right - width : 0) + offsetX,
              y: pageHeight - (index + 1) * lineHeight - top - offsetY,
              // -(height - textHeight) / 2,
              width: lineWidth,
              height: lineHeight,
              borderWidth: 0,
              color: rgb(1, 1, 1),
              opacity: 0.5,
              borderOpacity: 0.75,
            });
          }

          if (pows[index].length === 0) {
            const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ].widthOfTextAtSize(line, fontSize);
            const differenceWidth = width - lineWidth;
            const offsetX =
              textAlign === 'center'
                ? differenceWidth / 2
                : ['right', 'end'].includes(textAlign)
                  ? differenceWidth
                  : 0;
            this.page!.drawText(line, {
              x:
                (left ? left : right ? pageWidth - right - width : 0) + offsetX,
              y:
                pageHeight -
                (index + 1) * lineHeight -
                top +
                spaceYFactor * 0.93 -
                offsetY,
              size: fontSize,
              lineHeight,
              color,
              font: this.fonts![capitalizeFirstLetter(fontFamily)][
                capitalizeFirstLetter(fontWeight)
              ],
              rotate: {
                type: 'radians' as any,
                angle: rotate,
              },
            });
          } else {
            const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
              capitalizeFirstLetter(fontWeight)
            ].widthOfTextAtSize(line, fontSize);
            const differenceWidth = width - lineWidth;

            let currentLineWidth =
              textAlign === 'center'
                ? differenceWidth / 2
                : ['right', 'end'].includes(textAlign)
                  ? differenceWidth
                  : 0;

            const { substrings, isIndexed } = splitByIndices(line, pows[index]);

            substrings.forEach((fLine, pIndex) => {
              if (isIndexed[pIndex] && ['M2'].includes(fLine)) {
                const ifLineWidth = this.fonts![
                  capitalizeFirstLetter(fontFamily)
                ][capitalizeFirstLetter(fontWeight)].widthOfTextAtSize(
                  fLine[0].toLocaleLowerCase(),
                  fontSize,
                );

                this.page!.drawText(fLine[0].toLocaleLowerCase(), {
                  x:
                    currentLineWidth +
                    (left ? left : right ? pageWidth - right - width : 0),
                  y:
                    pageHeight -
                    (index + 1) * lineHeight -
                    top +
                    spaceYFactor * 0.93 -
                    offsetY,
                  size: fontSize,
                  lineHeight,
                  color,
                  font: this.fonts![capitalizeFirstLetter(fontFamily)][
                    capitalizeFirstLetter(fontWeight)
                  ],
                  rotate: {
                    type: 'radians' as any,
                    angle: rotate,
                  },
                });

                this.page!.drawText(fLine[1], {
                  x:
                    ifLineWidth +
                    currentLineWidth +
                    (left ? left : right ? pageWidth - right - width : 0),
                  y:
                    pageHeight -
                    (index + 0.75) * lineHeight -
                    top +
                    spaceYFactor * 0.93 -
                    offsetY,
                  size: fontSize * 0.7,
                  lineHeight,
                  color,
                  font: this.fonts![capitalizeFirstLetter(fontFamily)][
                    capitalizeFirstLetter(fontWeight)
                  ],
                  rotate: {
                    type: 'radians' as any,
                    angle: rotate,
                  },
                });

                currentLineWidth +=
                  ifLineWidth +
                  this.fonts![capitalizeFirstLetter(fontFamily)][
                    capitalizeFirstLetter(fontWeight)
                  ].widthOfTextAtSize(fLine[1], fontSize * 0.7);
              } else {
                this.page!.drawText(fLine, {
                  x:
                    currentLineWidth +
                    (left ? left : right ? pageWidth - right - width : 0),
                  y:
                    pageHeight -
                    (index + 1) * lineHeight -
                    top +
                    spaceYFactor * 0.93 -
                    offsetY,
                  size: fontSize,
                  lineHeight,
                  color,
                  font: this.fonts![capitalizeFirstLetter(fontFamily)][
                    capitalizeFirstLetter(fontWeight)
                  ],
                  rotate: {
                    type: 'radians' as any,
                    angle: rotate,
                  },
                });
                currentLineWidth += this.fonts![
                  capitalizeFirstLetter(fontFamily)
                ][capitalizeFirstLetter(fontWeight)].widthOfTextAtSize(
                  fLine,
                  fontSize,
                );
              }
            });
          }
        });
        break;
      }
    }
  }

  public calculeFontSize({
    text = '',
    width,
    height,
    fontFamily = 'Primary',
    fontWeight = 'Bold',
    spaceX = 0,
  }: any) {
    if (!(width && height)) return 0;
    const words = [text];
    const lineHeightMultiplier = 1 / 1.045;
    for (
      let currentFontSize = this.minFontSize;
      currentFontSize <= this.maxFontSize;
      currentFontSize++
    ) {
      const lineHeight =
        this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].heightAtSize(currentFontSize) * lineHeightMultiplier;

      let line = '';
      let result: any[] = [];
      for (let n = 0; n < words.length; n++) {
        const testLine = line + (n > 0 ? ' ' : '') + words[n];
        const testWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(testLine, currentFontSize);
        if (testWidth > width - spaceX * currentFontSize) {
          result.push(line);
          line = words[n];
        } else {
          line = testLine;
        }
      }
      result.push(line);
      result = result.filter((line) => line);
      const textHeight = result.length * lineHeight;
      let maxTextLineWidth = 0;

      result.forEach((line) => {
        const testWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(line, currentFontSize);
        if (testWidth > maxTextLineWidth) maxTextLineWidth = testWidth;
      });

      if (
        textHeight > height ||
        maxTextLineWidth > width - spaceX * currentFontSize
      ) {
        return currentFontSize - 1;
      }
    }
    return 0;
  }

  public validity({
    text = '',
    top = 0,
    left = 0,
    right,
    width = 0,
    height = 0,
    fontFamily = 'Primary',
    fontWeight = 'Bold',
  }: NodeProps) {
    const { width: pageWidth, height: pageHeight } = this.page!.getSize();
    const lineHeightMultiplier = 1 / 1.045;
    if (this.isDev()) {
      this.page!.drawRectangle({
        x: left ? left : right ? pageWidth - right - width : 0,
        y: pageHeight - height - top,
        width: width,
        height: height,
        borderWidth: 0,
        color: rgb(0.75, 0.2, 0.2),
        opacity: 0.5,
        borderOpacity: 0.75,
      });
    }
    const font =
      this.fonts![capitalizeFirstLetter(fontFamily)][
        capitalizeFirstLetter(fontWeight)
      ];

    const cText = 'VALIDADE';
    const spaceFactor = 0.26;
    const underlineFactor = 0.72;
    const fontSize = this.calculeFontSize({
      text: cText,
      width,
      height,
      fontFamily,
      fontWeight,
      spaceX: 3 * spaceFactor + 8 * underlineFactor,
    });
    const spaceWidth = spaceFactor * fontSize;
    const underlineWidth = underlineFactor * fontSize;
    this.page!.drawText(cText, {
      x: left,
      y: pageHeight - top - (fontSize ?? 0) * lineHeightMultiplier,
      size: fontSize,
      font,
    });
    const date = text.split('/');
    const day = date[0] ?? '';
    const month = date[1] ?? '';
    const year = date[2] ?? '';
    const widthDay = font.widthOfTextAtSize(day, fontSize);
    const widthMonth = font.widthOfTextAtSize(month, fontSize);
    const widthYear = font.widthOfTextAtSize(year, fontSize);
    const widthText = font.widthOfTextAtSize(cText, fontSize);

    const y = pageHeight - top - (fontSize ?? 0) * lineHeightMultiplier;

    this.page!.drawText(day, {
      x: left + widthText + spaceWidth + (2 * underlineWidth - widthDay) / 2,
      y,
      size: fontSize,
      font,
    });

    this.page!.drawLine({
      start: { x: left + widthText + spaceWidth, y: y - 0.15 * fontSize },
      end: {
        x: left + widthText + spaceWidth + 2 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      thickness: 2,
    });

    const point1 = rotatePoint(
      {
        x: left + widthText + spaceWidth + 2 * underlineWidth,
        y: y + fontSize,
      },
      {
        x: left + widthText + spaceWidth + 2 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      -15,
    );

    this.page!.drawLine({
      start: {
        x: left + widthText + spaceWidth + 2 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      end: {
        x: point1.x,
        y: point1.y,
      },
      thickness: 2,
    });

    this.page!.drawText(month, {
      x:
        left +
        widthText +
        spaceWidth +
        2 * underlineWidth +
        spaceWidth +
        (2 * underlineWidth - widthMonth) / 2,
      y,
      size: fontSize,
      font,
    });

    this.page!.drawLine({
      start: {
        x: left + widthText + 2 * spaceWidth + 2 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      end: {
        x: left + widthText + 2 * spaceWidth + 4 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      thickness: 2,
    });

    const point2 = rotatePoint(
      {
        x: left + widthText + 2 * spaceWidth + 4 * underlineWidth,
        y: y + fontSize,
      },
      {
        x: left + widthText + 2 * spaceWidth + 4 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      -15,
    );

    this.page!.drawLine({
      start: {
        x: left + widthText + 2 * spaceWidth + 4 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      end: {
        x: point2.x,
        y: point2.y,
      },
      thickness: 2,
    });

    this.page!.drawText(year, {
      x:
        left +
        widthText +
        spaceWidth +
        2 * spaceWidth +
        4 * underlineWidth +
        (4 * underlineWidth - widthYear) / 2,
      y,
      size: fontSize,
      font,
    });

    this.page!.drawLine({
      start: {
        x: left + widthText + 3 * spaceWidth + 4 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      end: {
        x: left + widthText + 3 * spaceWidth + 8 * underlineWidth,
        y: y - 0.15 * fontSize,
      },
      thickness: 2,
    });
  }

  async economizePrimary({
    text,
    top = 0,
    left,
    right = 0,
    width,
    height,
    nowrap,
    textAlign,
    alignItems,
    color,
    fontFamily,
    fontWeight,
  }: NodeProps) {
    if (!(width && height)) return;
    const { width: pageWidth, height: pageHeight } = this.page!.getSize();
    const imageBytes = await fetch('/economize.png').then(
      async (res) => await res.arrayBuffer(),
    );
    const image = await this.pdfDoc!.embedPng(imageBytes);
    this.page!.drawImage(image, {
      x: left ? left : right ? pageWidth - right - width : 0,
      y: pageHeight - height - top,
      width: width,
      height: height,
    });
    this.textNode({
      text,
      width: width * 0.7,
      height: height * 0.55,
      top: top + height * 0.3,
      left: left,
      right: right + width * 0.15,
      fontFamily,
      color,
      textAlign,
      alignItems,
      nowrap,
      fontWeight,
    });
  }

  async economizeSecondary({
    text,
    top = 0,
    left = 0,
    right = 0,
    width,
    height,
    nowrap,
    textAlign,
    alignItems,
    color,
    fontFamily,
    fontWeight,
    borderRadius = 0,
    background = [255, 255, 255, 0],
  }: NodeProps) {
    if (!(width && height)) return;
    const { height: pageHeight } = this.page!.getSize();
    this.drawRoundedRectangle(
      pageHeight - top - height,
      left,
      width,
      height,
      +borderRadius * 0.73,
      rgb(background[0] / 255, background[1] / 255, background[2] / 255),
    );
    this.textNode({
      text: 'ECONOMIZE',
      width: 0.75 * width,
      height: 0.4 * height,
      top: top + 0.05 * height,
      left: left + 0.125 * width,
      color,
      textAlign,
      alignItems,
      nowrap,
      fontFamily,
      fontWeight,
    });
    this.textNode({
      text,
      width: 0.7 * width,
      height: 0.65 * height,
      top: top + 0.3 * height,
      left: left + 0.15 * width,
      color,
      textAlign,
      alignItems,
      nowrap,
      fontFamily,
      fontWeight,
    });
  }

  public async economize(props: NodeProps) {
    switch (props.variant ?? 'primary') {
      case 'primary':
        await this.economizePrimary(props);
        break;
        break;
      case 'secondary':
        await this.economizeSecondary(props);
        break;
      default:
        await this.economizePrimary(props);
        break;
    }
  }

  public rotateTextNode({
    text = '',
    top = 0,
    left,
    right,
    width,
    height,
    textAlign = 'center',
    color: [r, g, b] = [0, 0, 0],
    background: [br, bg, bb, ba] = [0, 0, 0, 0],
    rotate = 0,
    fontFamily = 'Primary',
    fontWeight = 'Bold',
  }: NodeProps) {
    if (!(width && height)) return;
    const [vX, vY] = [Math.cos(rotate), Math.sin(rotate)];
    const color = rgb(r / 255, g / 255, b / 255);
    const { width: pageWidth, height: pageHeight } = this.page!.getSize();
    const lineHeightMultiplier = 1 / 1.045;
    const lineFactor = 1 / 1.4;
    for (
      let currentFontSize = this.minFontSize;
      currentFontSize <= this.maxFontSize;
      currentFontSize++
    ) {
      const lineHeight =
        this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].heightAtSize(currentFontSize) * lineHeightMultiplier;
      if (lineHeight > height) {
        const fontSize = currentFontSize - 1;
        const lineHeight =
          this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ].heightAtSize(fontSize) * lineHeightMultiplier;
        const spaceYFactor = (lineHeightMultiplier - lineFactor) * fontSize;
        if (ba) {
          this.page!.drawRectangle({
            x: left ? left : right ? pageWidth - right - width : 0,
            y: pageHeight - height - top,
            width: width,
            height: height,
            borderWidth: 0,
            color: rgb(br / 255, bg / 255, bb / 255),
            opacity: ba,
            borderOpacity: ba,
          });
        }
        if (this.isDev()) {
          this.page!.drawRectangle({
            x: left ? left : right ? pageWidth - right - width : 0,
            y: pageHeight - height - top,
            width: width,
            height: height,
            borderWidth: 0,
            color: rgb(0.75, 0.2, 0.2),
            opacity: 0.5,
            borderOpacity: 0.75,
            rotate: {
              type: 'radians' as any,
              angle: rotate,
            },
          });
        }
        const lineWidth = this.fonts![capitalizeFirstLetter(fontFamily)][
          capitalizeFirstLetter(fontWeight)
        ].widthOfTextAtSize(text, fontSize);
        const differenceWidth = width - lineWidth;
        const offsetX =
          textAlign === 'center'
            ? differenceWidth / 2
            : ['right', 'end'].includes(textAlign)
              ? differenceWidth
              : 0;

        if (this.isDev()) {
          this.page!.drawRectangle({
            x: (left ?? 0 - width) + offsetX * vX,
            y: pageHeight - lineHeight - top + offsetX * vY,
            width: lineWidth,
            height: lineHeight,
            borderWidth: 0,
            color: rgb(1, 1, 1),
            opacity: 0.5,
            borderOpacity: 0.75,
            rotate: {
              type: 'radians' as any,
              angle: rotate,
            },
          });
        }

        this.page!.drawText(text, {
          x: (left ?? 0 - width) + offsetX * vX + spaceYFactor * 0.93 * vY,
          y:
            pageHeight -
            lineHeight -
            top +
            spaceYFactor * 0.93 * vX +
            offsetX * vY,
          size: fontSize,
          lineHeight,
          color,
          font: this.fonts![capitalizeFirstLetter(fontFamily)][
            capitalizeFirstLetter(fontWeight)
          ],
          rotate: {
            type: 'radians' as any,
            angle: rotate,
          },
        });
        break;
      }
    }
  }

  public async create(
    page: [number, number],
    fonts: Font[],
    imageBytes?: ArrayBuffer,
    enabledBackground?: boolean,
    xOldPriceBytes?: ArrayBuffer,
  ): Promise<this> {
    this.pdfDoc = await PDFDocument.create();
    this.pdfDoc.registerFontkit(fontkit);
    this.page = this.pdfDoc.addPage(page);
    await new Promise((resolve) => setTimeout(resolve, 1));
    this.images.xOldPrice = await this.pdfDoc!.embedPng(xOldPriceBytes!);
    this.fonts = (
      await Promise.all(
        fonts.map(async ({ fontFamily, fontWeight, buffer }) => ({
          fontFamily,
          fontWeight,
          fontPDF: await this.pdfDoc!.embedFont(buffer),
        })),
      )
    ).reduce(
      (acc, { fontFamily, fontWeight, fontPDF }) => ({
        ...acc,
        [fontFamily]: {
          ...(() => acc[fontFamily] ?? {})(),
          [fontWeight]: fontPDF,
        },
      }),
      {},
    );

    if (enabledBackground && imageBytes) {
      const image = await this.loadImage(imageBytes);
      this.page!.drawImage(image, {
        width: page[0],
        height: page[1],
      });
    }
    return this;
  }

  private loadImage(imageBytes: ArrayBuffer) {
    return new Promise<PDFImage>((resolve, reject) => {
      this.pdfDoc!.embedJpg(imageBytes)
        .then(resolve)
        .catch(() => {
          this.pdfDoc!.embedPng(imageBytes).then(resolve).catch(reject);
        });
    });
  }

  private isDev() {
    return this.environment === 'development';
  }

  private drawRoundedRectangle(
    top = 0,
    left,
    width,
    height,
    borderRadius,
    color,
  ) {
    const page = this.page!;

    page.pushOperators(
      pushGraphicsState(),
      moveTo(left + borderRadius, top),
      lineTo(left + width - borderRadius, top),
      lineTo(left + width, top + borderRadius),
      lineTo(left + width, top + height - borderRadius),
      lineTo(left + width - borderRadius, top + height),
      lineTo(left + borderRadius, top + height),
      lineTo(left, top + height - borderRadius),
      lineTo(left, top + borderRadius),
      closePath(),
      setFillingColor(color),
      fill(),
      popGraphicsState(),
    );

    page.drawCircle({
      x: left + borderRadius,
      y: top + borderRadius,
      size: borderRadius,
      color,
    });

    page.drawCircle({
      x: left + width - borderRadius,
      y: top + borderRadius,
      size: borderRadius,
      color,
    });

    page.drawCircle({
      x: left + borderRadius,
      y: top + height - borderRadius,
      size: borderRadius,
      color,
    });

    page.drawCircle({
      x: left + width - borderRadius,
      y: top + height - borderRadius,
      size: borderRadius,
      color,
    });
  }

  private drawInclinedRectangle(x, y, width, height, angle, color) {
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);

    this.page!.pushOperators(
      pushGraphicsState(),
      moveTo(x, y),
      lineTo(x + width * cosAngle, y + width * sinAngle),
      lineTo(
        x + width * cosAngle + height * -sinAngle,
        y + width * sinAngle + height * cosAngle,
      ),
      lineTo(x + height * -sinAngle, y + height * cosAngle),
      closePath(),
      setFillingColor(color),
      fill(),
      popGraphicsState(),
    );
  }
}
